import { Injectable, Signal, computed, signal } from '@angular/core';
import { UploadStatus } from '../interfaces/upload/IUpload';
import { UploadProgressOperations } from '../operations/upload-progress.operations';
import { IUploadProgressTracker, UploadProgressItem, UploadProgressStartPayload } from '../interfaces/upload/upload-progress.interface';

@Injectable({
  providedIn: 'root',
})
export class UploadProgressService implements IUploadProgressTracker {
  private readonly uploadsSignal = signal<UploadProgressItem[]>([]);
  private readonly cleanupTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly successCleanupDelayMs = 1000;
  private readonly failureCleanupDelayMs = 4000;

  public readonly uploads: Signal<UploadProgressItem[]> = computed(() =>
    [...this.uploadsSignal()].sort((first, second) => second.updatedAt - first.updatedAt),
  );

  public startUpload(payload: UploadProgressStartPayload): void {
    this.clearCleanupTimer(payload.uploadId);

    const now = Date.now();
    const existingUpload = this.uploadsSignal().find(upload => upload.uploadId === payload.uploadId);
    const displayNameKey = payload.displayNameKey ?? UploadProgressOperations.getDefaultDisplayName(payload.fileType);

    if (existingUpload) {
      this.uploadsSignal.update(items =>
        items.map(item =>
          item.uploadId === payload.uploadId
            ? {
                ...item,
                fileType: payload.fileType,
                fileName: payload.fileName,
                displayNameKey,
                status: UploadStatus.READY_FOR_UPLOAD,
                errorMessage: undefined,
                updatedAt: now,
              }
            : item,
        ),
      );
      return;
    }

    this.uploadsSignal.update(items => [
      ...items,
      {
        uploadId: payload.uploadId,
        fileType: payload.fileType,
        fileName: payload.fileName,
        displayNameKey,
        status: UploadStatus.READY_FOR_UPLOAD,
        startedAt: now,
        updatedAt: now,
      },
    ]);
  }

  public updateUploadStatus(uploadId: string, status: UploadStatus, errorMessage?: string): void {
    this.updateUpload(uploadId, status, errorMessage);
  }

  public completeUpload(uploadId: string, status: UploadStatus, errorMessage?: string): void {
    const updatedUpload = this.updateUpload(uploadId, status, errorMessage);

    if (updatedUpload) {
      return;
    }

    const currentUpload = this.uploadsSignal().find(upload => upload.uploadId === uploadId);

    if (currentUpload && UploadProgressOperations.isTerminalStatus(currentUpload.status)) {
      this.scheduleCleanup(currentUpload.uploadId, currentUpload.status);
    }
  }

  public dismissUpload(uploadId: string): void {
    this.clearCleanupTimer(uploadId);
    this.uploadsSignal.update(items => items.filter(item => item.uploadId !== uploadId));
  }

  private updateUpload(uploadId: string, status: UploadStatus, errorMessage?: string): UploadProgressItem | undefined {
    let updatedUpload: UploadProgressItem | undefined;

    this.uploadsSignal.update(items => {
      const uploadIndex = items.findIndex(item => item.uploadId === uploadId);

      if (uploadIndex === -1) {
        return items;
      }

      const currentUpload = items[uploadIndex];
      const sameStatus = currentUpload.status === status;
      const sameErrorMessage = (currentUpload.errorMessage ?? '') === (errorMessage ?? '');

      if ((sameStatus && sameErrorMessage) || !UploadProgressOperations.canTransition(currentUpload.status, status)) {
        return items;
      }

      updatedUpload = {
        ...currentUpload,
        status,
        errorMessage,
        updatedAt: Date.now(),
      };

      return items.map(item => (item.uploadId === uploadId ? updatedUpload : item));
    });

    if (!updatedUpload) {
      return undefined;
    }

    if (UploadProgressOperations.isTerminalStatus(updatedUpload.status)) {
      this.scheduleCleanup(updatedUpload.uploadId, updatedUpload.status);
    }

    return updatedUpload;
  }

  private scheduleCleanup(uploadId: string, status: UploadStatus): void {
    this.clearCleanupTimer(uploadId);
    const delay =
      UploadProgressOperations.isErrorStatus(status) || status === UploadStatus.USER_CANCELLED
        ? this.failureCleanupDelayMs
        : this.successCleanupDelayMs;
    const timerHandle = setTimeout(() => this.dismissUpload(uploadId), delay);
    this.cleanupTimers.set(uploadId, timerHandle);
  }

  private clearCleanupTimer(uploadId: string): void {
    const existingTimer = this.cleanupTimers.get(uploadId);

    if (!existingTimer) {
      return;
    }

    clearTimeout(existingTimer);
    this.cleanupTimers.delete(uploadId);
  }
}
